// Shared by the browser, inbound processing and the authorized historical audit.
const normalized = value => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').replace(/[\s_-]/g, '').toLowerCase();
export function terminalKind(record) {
  const stages = [record?.stage, record?._crm_estagio, record?.estagio, record?.status].map(normalized);
  if (stages.some(s => ['closed','fechouvenda','venda','virouvenda','posvenda','nextsale'].includes(s))) return 'sold';
  if (record?.desqualificado === true || stages.some(s =>
    ['seminteresse','desqualificado','semresposta'].includes(s))) return 'reopen';
  return null;
}
export function terminalBoundary(record) {
  const kind = terminalKind(record);
  const fields = kind === 'sold'
    ? [record?.saleConfirmedAt, record?.manualSaleBinding?.boundAt, record?.fechou_venda_em,
      record?.data_fechamento, record?.stageDates?.closed, record?.stageDates?.venda,
      record?.closedAt, record?.soldAt]
    : [record?.desqualificado_em, record?.desqualificadoEm, record?.disqualifiedAt,
      record?.disqualified_at, record?.stageDates?.semInteresse, record?.stageDates?.sem_interesse,
      record?.stageDates?.desqualificado, record?.responseWindow?.closedAt,
      record?.semInteresseAt, record?.closedAt];
  const values = fields.filter(v => v && Number.isFinite(Date.parse(v)));
  // A later manual terminal action wins over an earlier terminal event.
  if (values.length) return values.sort((a,b) => Date.parse(b)-Date.parse(a))[0];
  return record?.updatedAt && Number.isFinite(Date.parse(record.updatedAt)) ? record.updatedAt : null;
}
function afterBoundary(at, boundary) {
  if (!boundary) return false;
  if (/^\d{4}-\d\d-\d\d$/.test(String(boundary))) {
    const day = new Intl.DateTimeFormat('en-CA',{timeZone:'America/Sao_Paulo',
      year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date(at));
    return day > boundary;
  }
  return at > Date.parse(boundary);
}
export function terminalReplyIsNew(record, inboundAt, {live=false, now=Date.now()} = {}) {
  const at = Date.parse(inboundAt);
  if (!terminalKind(record) || !Number.isFinite(at) || at > now + 60000) return false;
  const marker = record.terminalReplyRequired;
  if (marker?.inboundAt && Date.parse(marker.inboundAt) === at) {
    const boundary = terminalBoundary({...record,updatedAt:null});
    // A subsequent manual terminal action must not resurrect an old obligation.
    if (boundary && Date.parse(boundary) > Date.parse(marker.observedAt)) return false;
    return true;
  }
  const boundary = terminalBoundary(record);
  if (boundary) {
    if (afterBoundary(at,boundary)) return true;
    // A date-only closure cannot order messages within the same day. A fresh
    // live event can observe that closure; a historical snapshot cannot.
    if (/^\d{4}-\d\d-\d\d$/.test(String(boundary)) && live && now-at >= 0 && now-at <= 5*60000) {
      const day = new Intl.DateTimeFormat('en-CA',{timeZone:'America/Sao_Paulo',
        year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date(at));
      return day === boundary;
    }
    return false;
  }
  // In live processing a newly persisted, fresh message observes the terminal
  // card before transition. Historical data without a boundary needs review.
  return live && now-at >= 0 && now-at <= 5*60000;
}
export function terminalInboundPatch(record, kind, message, {
  live=false, now=Date.now(), source='canonical_inbound',
} = {}) {
  if (!message || message.remetente !== 'cliente'
    || !terminalReplyIsNew(record,message.timestamp,{live,now})) return null;
  if (record.terminalReplyRequired?.messageId
    && record.terminalReplyRequired.messageId === (message.providerMessageId || message.waMessageId)) return null;
  const previousStage = record.stage || record._crm_estagio || record.estagio || record.status || '';
  const patch = {
    updatedAt:new Date(now).toISOString(),
    terminalReplyRequired:{
      inboundAt:message.timestamp,
      messageId:message.providerMessageId || message.waMessageId || '',
      observedAt:new Date(now).toISOString(), previousStage, source,
    },
  };
  if (terminalKind(record) === 'reopen') {
    const next = kind === 'prospect' ? 'respondeu' : kind === 'client' ? 'waiting' : 'chegou';
    patch[kind === 'lead' ? 'estagio' : 'stage'] = next;
    if (kind === 'lead') {
      patch.desqualificado=false;
      patch._crm_estagio=next;
      if (record.stage != null) patch.stage=next;
      if (record.status != null) patch.status='novo';
      patch.ultima_atualizacao=new Date(now).toISOString();
    }
    if (kind === 'prospect') patch.responseWindow={
      ...(record.responseWindow||{}),version:1,status:'replied',repliedAt:message.timestamp,
    };
    patch.reopenedAt=new Date(now).toISOString();
  }
  return patch;
}
